import React, { useState, useEffect } from 'react';
import {
  fetchWorkOrders, fetchWorkOrdersByCustomer, fetchCustomer, updateCustomer, fetchJobsByCustomer,
  getQuotes, fetchChoices, fetchWorkOrder, fetchAppointmentsByCustomer, fetchAppointmentTypes,
  fetchSchedulingSettings,
} from './api';
import './customerworkorderlist.css';
import WorkOrderModal from './workordermodal';
import NewWorkOrderModal from './newworkordermodal';
import JobModal from './jobs/jobmodal';
import NewJobModal from './jobs/newjobmodal';
import QuoteOrderModal from './quotes/quoteordermodal';
import AppointmentModal from './scheduling/appointmentmodal';
import EmailOptOutControl from './customers/EmailOptOutControl';
import QuickBooksCustomerPicker from './QuickBooksCustomerPicker';
import PaymentBadges from './PaymentBadges';
import { formatDateDisplay, getTodayEastern, getTomorrowEastern, isTodayEastern, isTomorrowEastern } from '../utils/timezone';

const SHADED_APPOINTMENT_STATUSES = ['arrived', 'cancelled'];

const CustomerWorkOrderList = ({ customerId }) => {
  const [customer, setCustomer] = useState(null);
  const [workOrders, setWorkOrders] = useState([]);
  const [jobs, setJobs] = useState([]);
  // Bumped to reload this customer's work orders and jobs after a change (a work order change
  // can change its job too, e.g. a new work order adds a job)
  const [refreshKey, setRefreshKey] = useState(0);
  const [quotes, setQuotes] = useState([]);
  const [selectedService, setSelectedService] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [showNewWorkOrderModal, setShowNewWorkOrderModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editedCustomer, setEditedCustomer] = useState(null);
  const [saveError, setSaveError] = useState('');
  const [showJobModal, setShowJobModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showNewJobModal, setShowNewJobModal] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [listType, setListType] = useState('workorders'); // 'workorders', 'jobs', 'quotes', or 'appointments'
  const [appointments, setAppointments] = useState([]);
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [schedulingSettings, setSchedulingSettings] = useState(null);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [choices, setChoices] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [jobSearchQuery, setJobSearchQuery] = useState('');
  const [selectedJobService, setSelectedJobService] = useState('');
  const [selectedJobDate, setSelectedJobDate] = useState('');
  const [selectedJobContact, setSelectedJobContact] = useState('');
  const [quoteSearchQuery, setQuoteSearchQuery] = useState('');
  const [selectedQuoteService, setSelectedQuoteService] = useState('');
  const [selectedQuoteDate, setSelectedQuoteDate] = useState('');
  
  // Pagination state for jobs
  const [currentJobPage, setCurrentJobPage] = useState(1);
  const [totalJobPages, setTotalJobPages] = useState(1);
  const [totalJobCount, setTotalJobCount] = useState(0);
  const [jobPaginationInfo, setJobPaginationInfo] = useState({});

  // Pagination state for quotes
  const [currentQuotePage, setCurrentQuotePage] = useState(1);
  const [totalQuotePages, setTotalQuotePages] = useState(1);
  const [totalQuoteCount, setTotalQuoteCount] = useState(0);
  const [quotePaginationInfo, setQuotePaginationInfo] = useState({});

  useEffect(() => {
    const loadCustomerData = async () => {
      try {
        const customerData = await fetchCustomer(customerId);
        setCustomer(customerData);
        setEditedCustomer(customerData);
      } catch (error) {
        console.error('Error loading customer data:', error);
      }
    };

    loadCustomerData();
  }, [customerId]);

  // Loaded once, needed by AppointmentModal (service dropdown, price/date-promised calc).
  useEffect(() => {
    const loadSchedulingContext = async () => {
      try {
        const [types, settings] = await Promise.all([fetchAppointmentTypes(), fetchSchedulingSettings()]);
        setAppointmentTypes(types || []);
        setSchedulingSettings(settings);
      } catch (error) {
        console.error('Error loading scheduling context:', error);
      }
    };
    loadSchedulingContext();
  }, []);

  useEffect(() => {
    if (!customer?.id) return;
    const loadAppointments = async () => {
      try {
        const response = await fetchAppointmentsByCustomer(customer.id);
        setAppointments((response && response.results) || []);
      } catch (error) {
        console.error('Error loading appointments:', error);
        setAppointments([]);
      }
    };
    loadAppointments();
  }, [customer]);

  // Load choices from API
  useEffect(() => {
    const loadChoices = async () => {
      try {
        const choicesData = await fetchChoices();
        setChoices(choicesData);
      } catch (error) {
        console.error('Error loading choices:', error);
      }
    };
    loadChoices();
  }, []);

  useEffect(() => {
    const loadWorkOrders = async () => {
      // Fetch both archived and non-archived work orders for the customer
      const [nonArchivedWorkOrders, archivedWorkOrders] = await Promise.all([
        fetchWorkOrdersByCustomer(selectedService, selectedDate, customerId, selectedStatus, false),
        fetchWorkOrdersByCustomer(selectedService, selectedDate, customerId, selectedStatus, true)
      ]);
      
      // Combine both lists - handle the paginated response structure
      const allWorkOrders = [
        ...(nonArchivedWorkOrders?.results || []), 
        ...(archivedWorkOrders?.results || [])
      ];
      
      // Filter work orders by search query on the frontend
      let filteredWorkOrders = allWorkOrders;
      if (searchQuery.trim()) {
        filteredWorkOrders = allWorkOrders.filter(workOrder => 
          workOrder.vehicle?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          workOrder.service_requested?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          workOrder.id?.toString().includes(searchQuery) ||
          workOrder.status?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          workOrder.date_promised?.includes(searchQuery) ||
          workOrder.invoice_number?.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }
      setWorkOrders(filteredWorkOrders);
    };

    loadWorkOrders();
  }, [selectedService, selectedDate, selectedStatus, searchQuery, customerId, refreshKey]);

  useEffect(() => {
    const loadJobs = async () => {
      try {
        const response = await fetchJobsByCustomer(customerId, currentJobPage, 50);
        
        if (response && response.results) {
          // Apply filters to jobs
          let filteredJobs = response.results;
          
          // Filter by service
          if (selectedJobService) {
            filteredJobs = filteredJobs.filter(job => job.service === selectedJobService);
          }
          
          // Filter by contact
          if (selectedJobContact) {
            filteredJobs = filteredJobs.filter(job => job.contact === selectedJobContact);
          }
          
          // Filter by date using Eastern Time
          if (selectedJobDate) {
            if (selectedJobDate === 'today') {
              filteredJobs = filteredJobs.filter(job => isTodayEastern(job.date_promised));
            } else if (selectedJobDate === 'tomorrow') {
              filteredJobs = filteredJobs.filter(job => isTomorrowEastern(job.date_promised));
            }
          }
          
          // Filter by search query
          if (jobSearchQuery.trim()) {
            filteredJobs = filteredJobs.filter(job => 
              job.make?.toLowerCase().includes(jobSearchQuery.toLowerCase()) ||
              job.model?.toLowerCase().includes(jobSearchQuery.toLowerCase()) ||
              job.service?.toLowerCase().includes(jobSearchQuery.toLowerCase()) ||
              job.contact?.toLowerCase().includes(jobSearchQuery.toLowerCase()) ||
              job.id?.toString().includes(jobSearchQuery) ||
              job.date_promised?.includes(jobSearchQuery) ||
              job.work_order?.toString().includes(jobSearchQuery) ||
              job.ro?.toLowerCase().includes(jobSearchQuery.toLowerCase()) ||
              job.po?.toLowerCase().includes(jobSearchQuery.toLowerCase())
            );
          }
          
          setJobs(filteredJobs);
          setTotalJobPages(response.total_pages || 1);
          setTotalJobCount(response.count || 0);
          setJobPaginationInfo({
            hasNext: response.has_next || false,
            hasPrevious: response.has_previous || false,
            nextPage: response.next_page || null,
            previousPage: response.previous_page || null
          });
        } else {
          // Handle case where response doesn't have expected structure
          setJobs([]);
          setTotalJobPages(1);
          setTotalJobCount(0);
          setJobPaginationInfo({
            hasNext: false,
            hasPrevious: false,
            nextPage: null,
            previousPage: null
          });
        }
      } catch (error) {
        console.error('Error loading jobs:', error);
        setJobs([]);
        setTotalJobPages(1);
        setTotalJobCount(0);
        setJobPaginationInfo({
          hasNext: false,
          hasPrevious: false,
          nextPage: null,
          previousPage: null
        });
      }
    };

    loadJobs();
  }, [customerId, selectedJobService, selectedJobDate, selectedJobContact, jobSearchQuery, currentJobPage, refreshKey]);

  // Reset to page 1 when search query or filters change
  useEffect(() => {
    setCurrentJobPage(1);
  }, [jobSearchQuery, selectedJobService, selectedJobDate, selectedJobContact]);

  useEffect(() => {
    const loadQuotes = async () => {
      try {
        // Fetch both archived and non-archived quotes, scoped to this customer by the backend
        const [nonArchivedQuotes, archivedQuotes] = await Promise.all([
          getQuotes(false, currentQuotePage, 50, customerId), // Non-archived quotes
          getQuotes(true, currentQuotePage, 50, customerId)  // Archived quotes
        ]);

        // Combine both lists - handle the paginated response structure
        let filteredQuotes = [
          ...(nonArchivedQuotes?.results || []),
          ...(archivedQuotes?.results || [])
        ];

        // Filter by service
        if (selectedQuoteService) {
          filteredQuotes = filteredQuotes.filter(quote => quote.service_requested === selectedQuoteService);
        }
        
        // Filter by date using Eastern Time
        if (selectedQuoteDate) {
          if (selectedQuoteDate === 'today') {
            filteredQuotes = filteredQuotes.filter(quote => isTodayEastern(quote.date_promised));
          } else if (selectedQuoteDate === 'tomorrow') {
            filteredQuotes = filteredQuotes.filter(quote => isTomorrowEastern(quote.date_promised));
          }
        }
        
        // Filter by search query
        if (quoteSearchQuery.trim()) {
          filteredQuotes = filteredQuotes.filter(quote => 
            quote.vehicle?.toLowerCase().includes(quoteSearchQuery.toLowerCase()) ||
            quote.service_requested?.toLowerCase().includes(quoteSearchQuery.toLowerCase()) ||
            quote.customer_name?.toLowerCase().includes(quoteSearchQuery.toLowerCase()) ||
            quote.id?.toString().includes(quoteSearchQuery) ||
            quote.date_promised?.includes(quoteSearchQuery)
          );
        }
        
        setQuotes(filteredQuotes);
        
        console.log('=== FINAL QUOTES SET ===');
        console.log('Final filtered quotes count:', filteredQuotes.length);
        console.log('Final quotes:', filteredQuotes);
        console.log('========================');
        
        // Use the total count from non-archived quotes for pagination (since we're combining both)
        const totalCount = (nonArchivedQuotes?.count || 0) + (archivedQuotes?.count || 0);
        setTotalQuotePages(Math.ceil(totalCount / 50) || 1);
        setTotalQuoteCount(totalCount);
        setQuotePaginationInfo({
          hasNext: false, // Disable pagination since we're showing all quotes
          hasPrevious: false,
          nextPage: null,
          previousPage: null
        });
      } catch (error) {
        console.error('Error loading quotes:', error);
        setQuotes([]);
        setTotalQuotePages(1);
        setTotalQuoteCount(0);
        setQuotePaginationInfo({
          hasNext: false,
          hasPrevious: false,
          nextPage: null,
          previousPage: null
        });
      }
    };

    loadQuotes();
  }, [customerId, selectedQuoteService, selectedQuoteDate, quoteSearchQuery]);

  // Remove the separate useEffect for resetting quote page since we're not using pagination

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
    setSaveError('');
    if (!isEditing) {
      setEditedCustomer(customer);
    }
  };

  const handleCustomerUpdate = async () => {
    try {
      // Use the saved copy: the server tidies additional emails (blanks, repeats).
      const saved = await updateCustomer(editedCustomer);
      setCustomer(saved);
      setEditedCustomer(saved);
      setSaveError('');
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating customer:', error);
      const errors = error.response?.data;
      setSaveError(errors?.additional_emails
        ? 'One of the additional emails isn\'t a valid email address.'
        : errors?.additional_phones ? 'An additional phone is too long (20 characters max).'
        : errors?.email ? 'The email isn\'t a valid email address.' : 'Could not save. Try again.');
    }
  };

  // Edit one of the additional_emails / additional_phones lists.
  const setListItem = (field, index, value) => {
    setEditedCustomer(prev => ({
      ...prev,
      [field]: (prev[field] || []).map((item, i) => (i === index ? value : item)),
    }));
  };

  const addListItem = (field) => {
    setEditedCustomer(prev => ({ ...prev, [field]: [...(prev[field] || []), ''] }));
  };

  const removeListItem = (field, index) => {
    setEditedCustomer(prev => ({
      ...prev,
      [field]: (prev[field] || []).filter((_, i) => i !== index),
    }));
  };

  const renderListEditor = (field, label, inputType) => (
    <div className="customer-info-edit">
      <label className="customer-info-label">{label}</label>
      <span className="customer-info-hint">
        Other {inputType === 'email' ? 'emails' : 'phone numbers'} this customer has used, kept as a record.
        They aren't contacted or used to match bookings.
      </span>
      {(editedCustomer[field] || []).map((value, index) => (
        <div className="customer-additional-email-edit" key={index}>
          <input
            className="customer-info-input"
            type={inputType}
            value={value}
            onChange={(e) => setListItem(field, index, e.target.value)}
          />
          <button type="button" className="customer-additional-email-remove" onClick={() => removeListItem(field, index)}>
            Remove
          </button>
        </div>
      ))}
      <button type="button" className="customer-additional-email-add" onClick={() => addListItem(field)}>
        {inputType === 'email' ? '+ Add email' : '+ Add phone'}
      </button>
    </div>
  );

  const hasAdditional = customer
    && (customer.additional_emails?.length > 0 || customer.additional_phones?.length > 0);

  const handleInputChange = (field, value) => {
    setEditedCustomer(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const toggleNewWorkOrderModal = () => {
    setShowNewWorkOrderModal(!showNewWorkOrderModal);
  };

  const toggleNewJobModal = () => {
    setShowNewJobModal(!showNewJobModal);
  };

  const handleWorkOrderCreated = (newWorkOrder) => {
    setWorkOrders(prev => [...prev, newWorkOrder]);
    setShowNewWorkOrderModal(false);
    refreshLists();
  };

  const handleJobCreated = (newJob) => {
    setJobs(prev => [newJob, ...prev]);
    setShowNewJobModal(false);
    refreshLists();
  };

  // Reload the work orders and jobs in place after one is created or changed
  const refreshLists = () => setRefreshKey(key => key + 1);

  const toggleService = (service) => {
    setSelectedService(service);
  };

  const toggleDate = (date) => {
    setSelectedDate(date);
  };

  const toggleStatus = (status) => {
    setSelectedStatus(status);
  };

  const toggleListType = (type) => {
    console.log('=== TOGGLE LIST TYPE ===');
    console.log('Switching to:', type);
    console.log('Current quotes count:', quotes.length);
    console.log('Current quotes:', quotes);
    console.log('======================');
    setListType(type);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleJobSearchChange = (e) => {
    setJobSearchQuery(e.target.value);
  };

  const toggleJobService = (service) => {
    setSelectedJobService(service);
  };

  const toggleJobDate = (date) => {
    setSelectedJobDate(date);
  };

  const toggleJobContact = (contact) => {
    setSelectedJobContact(contact);
  };

  const handleQuoteSearchChange = (e) => {
    setQuoteSearchQuery(e.target.value);
  };

  const toggleQuoteService = (service) => {
    setSelectedQuoteService(service);
  };

  const toggleQuoteDate = (date) => {
    setSelectedQuoteDate(date);
  };

  const handleWorkOrderClick = (workOrder) => {
    setSelectedWorkOrder(workOrder);
    setShowModal(true);
  };

  // Listen for custom workOrderSelected event from notification clicks
  useEffect(() => {
    const handleWorkOrderSelected = (event) => {
      const workOrderId = event.detail.workOrderId;
      console.log('Received workOrderSelected event for work order ID:', workOrderId);
      
      // Prevent opening if modal is already open
      if (showModal) {
        console.log('Modal already open, ignoring workOrderSelected event');
        return;
      }
      
      // Clear localStorage to prevent duplicate opening
      localStorage.removeItem('selectedWorkOrderId');
      
      // If work orders are already loaded, try to find the work order
      if (workOrders.length > 0) {
        const workOrder = workOrders.find(wo => wo.id === parseInt(workOrderId));
        if (workOrder) {
          console.log('Found work order in loaded list, opening modal:', workOrder.id);
          setSelectedWorkOrder(workOrder);
          setShowModal(true);
        } else {
          // Fetch the work order directly
          console.log('Work order not in loaded list, fetching directly:', workOrderId);
          fetchWorkOrder(parseInt(workOrderId))
            .then(fetchedWorkOrder => {
              console.log('Fetched work order from event:', fetchedWorkOrder);
              setSelectedWorkOrder(fetchedWorkOrder);
              setShowModal(true);
            })
            .catch(error => {
              console.error('Error fetching work order from event:', error);
            });
        }
      } else {
        // Work orders not loaded yet, fetch the work order directly
        console.log('Work orders not loaded yet, fetching directly:', workOrderId);
        fetchWorkOrder(parseInt(workOrderId))
          .then(fetchedWorkOrder => {
            console.log('Fetched work order from event (no work orders loaded):', fetchedWorkOrder);
            setSelectedWorkOrder(fetchedWorkOrder);
            setShowModal(true);
          })
          .catch(error => {
            console.error('Error fetching work order from event (no work orders loaded):', error);
          });
      }
    };

    // Add event listener
    window.addEventListener('workOrderSelected', handleWorkOrderSelected);

    // Cleanup
    return () => {
      window.removeEventListener('workOrderSelected', handleWorkOrderSelected);
    };
  }, [workOrders, showModal]);

  const handleJobClick = (job) => {
    setSelectedJob(job);
    setShowJobModal(true);
  };

  const handleQuoteClick = (quote) => {
    setSelectedQuote(quote);
    setShowQuoteModal(true);
  };

  const handleAppointmentClick = (appointment) => {
    setSelectedAppointment(appointment);
    setShowAppointmentModal(true);
  };

  const handleAppointmentModalClose = (didChange) => {
    setShowAppointmentModal(false);
    setSelectedAppointment(null);
    if (didChange && customer?.id) {
      fetchAppointmentsByCustomer(customer.id)
        .then((response) => setAppointments((response && response.results) || []))
        .catch((error) => console.error('Error reloading appointments:', error));
    }
  };

  const handleJobUpdate = (updatedJob) => {
    setJobs(prev => prev.map(job => job.id === updatedJob.id ? updatedJob : job));
    refreshLists();
  };

  const handleWorkOrderUpdate = (updatedWorkOrder) => {
    console.log('=== CUSTOMER WORK ORDER LIST - handleWorkOrderUpdate ===');
    console.log('Updated work order:', updatedWorkOrder);
    console.log('Current status filter:', selectedStatus);
    console.log('Updated work order status:', updatedWorkOrder.status);
    
    // Check if the updated work order matches the current status filter
    const matchesCurrentFilter = !selectedStatus || updatedWorkOrder.status === selectedStatus;
    
    if (matchesCurrentFilter) {
      // Update the work order in the list
      setWorkOrders(prev => prev.map(wo => wo.id === updatedWorkOrder.id ? updatedWorkOrder : wo));
    } else {
      // Remove the work order from the list if it no longer matches the filter
      console.log('Work order no longer matches status filter, removing from list');
      setWorkOrders(prev => prev.filter(wo => wo.id !== updatedWorkOrder.id));
    }
    refreshLists();
  };

  const handleQuoteUpdate = (updatedQuote) => {
    // Update the quote in the list (keep all quotes, including archived ones)
    setQuotes(quotes.map(quote => quote.id === updatedQuote.id ? updatedQuote : quote));
  };

  // Pagination handlers for jobs
  const handleJobPageChange = (page) => {
    setCurrentJobPage(page);
  };

  const handleJobNextPage = () => {
    if (jobPaginationInfo.hasNext) {
      setCurrentJobPage(currentJobPage + 1);
    }
  };

  const handleJobPreviousPage = () => {
    if (jobPaginationInfo.hasPrevious) {
      setCurrentJobPage(currentJobPage - 1);
    }
  };

  // Pagination handlers for quotes
  const handleQuotePageChange = (page) => {
    setCurrentQuotePage(page);
  };

  const handleQuoteNextPage = () => {
    if (quotePaginationInfo.hasNext) {
      setCurrentQuotePage(currentQuotePage + 1);
    }
  };

  const handleQuotePreviousPage = () => {
    if (quotePaginationInfo.hasPrevious) {
      setCurrentQuotePage(currentQuotePage - 1);
    }
  };

  if (!customer) {
    return <div className="customer-portfolio">Loading customer data...</div>;
  }

  return (
    <div className="customer-portfolio">
      <div className="customer-portfolio-header">
        <h1 className="customer-portfolio-title">{customer.name}</h1>
        <button 
          className="customer-portfolio-edit-button"
          onClick={handleEditToggle}
        >
          {isEditing ? 'Cancel' : 'Edit Customer'}
        </button>
      </div>

      <div className="customer-info-section">
        {isEditing ? (
          <>
            <div className="customer-info-edit">
              <label className="customer-info-label">Name</label>
              <input
                className="customer-info-input"
                type="text"
                value={editedCustomer.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
              />
            </div>
            <div className="customer-info-edit">
              <label className="customer-info-label">Phone</label>
              <input
                className="customer-info-input"
                type="tel"
                value={editedCustomer.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
              />
            </div>
            <div className="customer-info-edit">
              <label className="customer-info-label">Email</label>
              <input
                className="customer-info-input"
                type="email"
                value={editedCustomer.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
              />
            </div>
            {renderListEditor('additional_phones', 'Additional Phones', 'tel')}
            {renderListEditor('additional_emails', 'Additional Emails', 'email')}
            <div className="customer-info-edit">
              <label className="customer-info-label">Address</label>
              <textarea
                className="customer-info-input"
                value={editedCustomer.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                rows="3"
              />
            </div>
            <div className="customer-info-edit">
              <label className="customer-info-label">Notes</label>
              <textarea
                className="customer-info-input"
                value={editedCustomer.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                rows="3"
              />
            </div>
            <div className="customer-info-edit">
              <label className="customer-info-label">QuickBooks Customer</label>
              <QuickBooksCustomerPicker
                value={{ id: editedCustomer.qb_customer_id || '', name: editedCustomer.qb_customer_name || '' }}
                onChange={({ id, name }) => setEditedCustomer(prev => ({ ...prev, qb_customer_id: id, qb_customer_name: name }))}
              />
            </div>
            <div className="customer-save-row">
              {saveError && <div className="customer-save-error" role="alert">{saveError}</div>}
              <button 
                className="customer-portfolio-edit-button"
                onClick={handleCustomerUpdate}
              >
                Save Changes
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="customer-info-group">
              <span className="customer-info-label">Phone</span>
              <span className="customer-info-value">{customer.phone}</span>
            </div>
            <div className="customer-info-group">
              <span className="customer-info-label">Email</span>
              <span className="customer-info-value">{customer.email}</span>
              <EmailOptOutControl email={customer.email} />
            </div>
            {/* Both cells render when either list has entries, so phones stay under
                Phone and emails under Email in the two-column grid. */}
            {hasAdditional && (
              <div className="customer-info-group">
                {customer.additional_phones?.length > 0 && (
                  <>
                    <span className="customer-info-label">Additional Phones</span>
                    {customer.additional_phones.map((phone) => (
                      <span className="customer-info-value" key={phone}>{phone}</span>
                    ))}
                  </>
                )}
              </div>
            )}
            {hasAdditional && (
              <div className="customer-info-group">
                {customer.additional_emails?.length > 0 && (
                  <>
                    <span className="customer-info-label">Additional Emails</span>
                    {customer.additional_emails.map((email) => (
                      <div className="customer-additional-email" key={email}>
                        <span className="customer-info-value">{email}</span>
                        <EmailOptOutControl email={email} />
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
            <div className="customer-info-group">
              <span className="customer-info-label">Address</span>
              <span className="customer-info-value">{customer.address}</span>
            </div>
            <div className="customer-info-group">
              <span className="customer-info-label">Notes</span>
              <span className="customer-info-value customer-info-notes">{customer.notes}</span>
            </div>
            <div className="customer-info-group">
              <span className="customer-info-label">QuickBooks Customer</span>
              <span className="customer-info-value">{customer.qb_customer_id ? customer.qb_customer_name : 'Not linked'}</span>
            </div>
          </>
        )}
      </div>

      <div className="unified-list-section">
        <div className="unified-list-header">
          <div className="list-type-toggle">
            <button 
              className={`list-type-button ${listType === 'workorders' ? 'active' : ''}`}
              onClick={() => toggleListType('workorders')}
            >
              Work Orders
            </button>
            <button 
              className={`list-type-button ${listType === 'jobs' ? 'active' : ''}`}
              onClick={() => toggleListType('jobs')}
            >
              Jobs
            </button>
            <button
              className={`list-type-button ${listType === 'quotes' ? 'active' : ''}`}
              onClick={() => toggleListType('quotes')}
            >
              Quotes
            </button>
            <button
              className={`list-type-button ${listType === 'appointments' ? 'active' : ''}`}
              onClick={() => toggleListType('appointments')}
            >
              Appointments
            </button>
          </div>
          
          {listType === 'workorders' && (
            <button 
              className="new-work-order-button"
              onClick={toggleNewWorkOrderModal}
            >
              New Work Order
            </button>
          )}
          
          {listType === 'jobs' && (
            <button 
              className="new-work-order-button"
              onClick={toggleNewJobModal}
            >
              New Job
            </button>
          )}
          
          {listType === 'quotes' && (
            <button 
              className="new-work-order-button"
              onClick={() => setShowQuoteModal(true)}
            >
              New Quote
            </button>
          )}
        </div>

        {listType === 'workorders' && (
          <div className="unified-list-filters">
            <div className="unified-list-search-group">
              <input
                className="unified-list-search-input"
                type="text"
                placeholder="Search work orders..."
                value={searchQuery}
                onChange={handleSearchChange}
              />
            </div>

            <div className="unified-list-filter-group">
              <label className="unified-list-filter-label">Status:
                <select 
                  className="unified-list-filter-select"
                  value={selectedStatus}
                  onChange={(e) => toggleStatus(e.target.value)}
                >
                  <option value="">All Work Orders</option>
                  <option value="In Process">In Process</option>
                  <option value="Called">Called</option>
                  <option value="Picked Up">Picked Up</option>
                </select>
              </label>
            </div>

            <div className="unified-list-filter-group">
              <label className="unified-list-filter-label">Service:
                <select 
                  className="unified-list-filter-select"
                  value={selectedService}
                  onChange={(e) => toggleService(e.target.value)}
                >
                  <option value="">All Services</option>
                  <option value="Restoration">Restoration</option>
                  <option value="Straightening">Straightening</option>
                  <option value="Repair Crack">Repair Crack</option>
                  <option value="Powder Coating">Powder Coating</option>
                  <option value="Mount & Balance">Mount & Balance</option>
                </select>
              </label>
            </div>

            <div className="unified-list-filter-group">
              <label className="unified-list-filter-label">Date:
                <select 
                  className="unified-list-filter-select"
                  value={selectedDate}
                  onChange={(e) => toggleDate(e.target.value)}
                >
                  <option value="">All Dates</option>
                  <option value="today">Today</option>
                  <option value="tomorrow">Tomorrow</option>
                </select>
              </label>
            </div>
          </div>
        )}

        {listType === 'jobs' && (
          <div className="unified-list-filters">
            <div className="unified-list-search-group">
              <input
                className="unified-list-search-input"
                type="text"
                placeholder="Search jobs..."
                value={jobSearchQuery}
                onChange={handleJobSearchChange}
              />
            </div>

            <div className="unified-list-filter-group">
              <label className="unified-list-filter-label">Service:
                <select 
                  className="unified-list-filter-select"
                  value={selectedJobService}
                  onChange={(e) => toggleJobService(e.target.value)}
                >
                  <option value="">All Services</option>
                  <option value="Restoration">Restoration</option>
                  <option value="Straightening">Straightening</option>
                  <option value="Repair Crack">Repair Crack</option>
                  <option value="Powder Coating">Powder Coating</option>
                  <option value="Mount & Balance">Mount & Balance</option>
                </select>
              </label>
            </div>

            <div className="unified-list-filter-group">
              <label className="unified-list-filter-label">Contact:
                <select 
                  className="unified-list-filter-select"
                  value={selectedJobContact}
                  onChange={(e) => toggleJobContact(e.target.value)}
                >
                  <option value="">All Contacts</option>
                  {choices.contact && choices.contact
                    .sort((a, b) => a.order - b.order)
                    .map(choice => (
                      <option key={choice.value} value={choice.value}>
                        {choice.display_name}
                      </option>
                    ))}
                </select>
              </label>
            </div>

            <div className="unified-list-filter-group">
              <label className="unified-list-filter-label">Date:
                <select 
                  className="unified-list-filter-select"
                  value={selectedJobDate}
                  onChange={(e) => toggleJobDate(e.target.value)}
                >
                  <option value="">All Dates</option>
                  <option value="today">Today</option>
                  <option value="tomorrow">Tomorrow</option>
                </select>
              </label>
            </div>
          </div>
        )}

        {listType === 'quotes' && (
          <div className="unified-list-filters">
            <div className="unified-list-search-group">
              <input
                className="unified-list-search-input"
                type="text"
                placeholder="Search quotes..."
                value={quoteSearchQuery}
                onChange={handleQuoteSearchChange}
              />
            </div>

            <div className="unified-list-filter-group">
              <label className="unified-list-filter-label">Service:
                <select 
                  className="unified-list-filter-select"
                  value={selectedQuoteService}
                  onChange={(e) => toggleQuoteService(e.target.value)}
                >
                  <option value="">All Services</option>
                  <option value="Restoration">Restoration</option>
                  <option value="Straightening">Straightening</option>
                  <option value="Repair Crack">Repair Crack</option>
                  <option value="Powder Coating">Powder Coating</option>
                  <option value="Mount & Balance">Mount & Balance</option>
                  <option value="Other">Other</option>
                </select>
              </label>
            </div>

            <div className="unified-list-filter-group">
              <label className="unified-list-filter-label">Date:
                <select 
                  className="unified-list-filter-select"
                  value={selectedQuoteDate}
                  onChange={(e) => toggleQuoteDate(e.target.value)}
                >
                  <option value="">All Dates</option>
                  <option value="today">Today</option>
                  <option value="tomorrow">Tomorrow</option>
                </select>
              </label>
            </div>
          </div>
        )}

        <div className="unified-list">
          {listType === 'workorders' && (
            <>
              <div className="unified-list-header-row" data-list-type="workorders">
                <div className="unified-list-cell">Vehicle</div>
                <div className="unified-list-cell">Service</div>
                <div className="unified-list-cell">Date Promised</div>
                <div className="unified-list-cell">Status</div>
                <div className="unified-list-cell">Work Order #</div>
              </div>
              
              {workOrders && workOrders.length > 0 ? (
                workOrders.map((order) => (
                  <div 
                    className={`unified-list-row ${order.is_archived ? 'archived-row' : ''}`} 
                    key={order.id} 
                    data-list-type="workorders"
                    onClick={() => handleWorkOrderClick(order)}
                  >
                    <div className="unified-list-cell">
                      {order.vehicle}
                      {order.is_archived && <span className="archived-indicator">📁 Archived</span>}
                    </div>
                    <div className="unified-list-cell">{order.service_requested}</div>
                    <div className="unified-list-cell">{order.date_promised}</div>
                    <div className="unified-list-cell">
                      {order.status}
                      <PaymentBadges workOrder={order} />
                    </div>
                    <div className="unified-list-cell">{order.id}</div>
                  </div>
                ))
              ) : (
                <div className="unified-list-row">
                  <div className="unified-list-cell" style={{ gridColumn: '1 / -1', textAlign: 'center' }}>
                    No work orders found
                  </div>
                </div>
              )}
            </>
          )}

          {listType === 'jobs' && (
            <>
              <div className="unified-list-header-row" data-list-type="jobs">
                <div className="unified-list-cell">Make</div>
                <div className="unified-list-cell">Service</div>
                <div className="unified-list-cell">Date Promised</div>
                <div className="unified-list-cell">Status</div>
                <div className="unified-list-cell">Job #</div>
              </div>
              
              {jobs && jobs.length > 0 ? (
                jobs.map((job) => (
                  <div 
                    className="unified-list-row" 
                    key={job.id} 
                    data-list-type="jobs"
                    onClick={() => handleJobClick(job)}
                  >
                    <div className="unified-list-cell">{job.make}</div>
                    <div className="unified-list-cell">{job.service}</div>
                    <div className="unified-list-cell">{job.date_promised}</div>
                    <div className="unified-list-cell">{job.status}</div>
                    <div className="unified-list-cell">{job.id}</div>
                  </div>
                ))
              ) : (
                <div className="unified-list-row">
                  <div className="unified-list-cell" style={{ gridColumn: '1 / -1', textAlign: 'center' }}>
                    No jobs found
                  </div>
                </div>
              )}
              
              {/* Jobs Pagination */}
              {listType === 'jobs' && totalJobPages > 1 && (
                <div className="unified-list-pagination">
                  <div className="unified-list-pagination-info">
                    Showing {((currentJobPage - 1) * 50) + 1} to {Math.min(currentJobPage * 50, totalJobCount)} of {totalJobCount} jobs
                  </div>
                  <div className="unified-list-pagination-controls">
                    <button 
                      className="unified-list-pagination-btn"
                      onClick={handleJobPreviousPage}
                      disabled={!jobPaginationInfo.hasPrevious}
                    >
                      ← Previous
                    </button>
                    
                    <div className="unified-list-pagination-pages">
                      {Array.from({ length: Math.min(5, totalJobPages) }, (_, i) => {
                        let pageNum;
                        if (totalJobPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentJobPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentJobPage >= totalJobPages - 2) {
                          pageNum = totalJobPages - 4 + i;
                        } else {
                          pageNum = currentJobPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            className={`unified-list-pagination-page ${
                              pageNum === currentJobPage ? 'active' : ''
                            }`}
                            onClick={() => handleJobPageChange(pageNum)}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    
                    <button 
                      className="unified-list-pagination-btn"
                      onClick={handleJobNextPage}
                      disabled={!jobPaginationInfo.hasNext}
                    >
                      Next →
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {listType === 'quotes' && (
            <>
              <div className="unified-list-header-row" data-list-type="quotes">
                <div className="unified-list-cell">Vehicle</div>
                <div className="unified-list-cell">Service</div>
                <div className="unified-list-cell">Date Promised</div>
                <div className="unified-list-cell">Price</div>
                <div className="unified-list-cell">Status</div>
                <div className="unified-list-cell">Quote #</div>
              </div>
              
              {quotes && quotes.length > 0 ? (
                quotes.map((quote) => (
                  <div 
                    className={`unified-list-row ${quote.is_archived ? 'archived-row' : ''}`}
                    key={quote.id} 
                    data-list-type="quotes"
                    onClick={() => handleQuoteClick(quote)}
                    style={quote.is_archived ? { opacity: 0.6, backgroundColor: '#f5f5f5' } : {}}
                  >
                    <div className="unified-list-cell">{quote.vehicle}</div>
                    <div className="unified-list-cell">{quote.service_requested}</div>
                    <div className="unified-list-cell">{quote.date_promised}</div>
                    <div className="unified-list-cell">${quote.price}</div>
                    <div className="unified-list-cell">
                      <span style={{ 
                        color: quote.is_archived ? '#666' : '#28a745',
                        fontWeight: 'bold',
                        fontSize: '12px'
                      }}>
                        {quote.is_archived ? 'ARCHIVED' : 'ACTIVE'}
                      </span>
                    </div>
                    <div className="unified-list-cell">{quote.id}</div>
                  </div>
                ))
              ) : (
                <div className="unified-list-row">
                  <div className="unified-list-cell" style={{ gridColumn: '1 / -1', textAlign: 'center' }}>
                    No quotes found
                  </div>
                </div>
              )}
            </>
          )}

          {listType === 'appointments' && (
            <>
              <div className="unified-list-header-row" data-list-type="appointments">
                <div className="unified-list-cell">Date / Time</div>
                <div className="unified-list-cell">Vehicle</div>
                <div className="unified-list-cell">Service</div>
                <div className="unified-list-cell">Status</div>
              </div>

              {appointments && appointments.length > 0 ? (
                appointments.map((appt) => (
                  <div
                    className="unified-list-row"
                    key={appt.id}
                    data-list-type="appointments"
                    onClick={() => handleAppointmentClick(appt)}
                    style={SHADED_APPOINTMENT_STATUSES.includes(appt.status) ? { opacity: 0.55 } : {}}
                  >
                    <div className="unified-list-cell">
                      {new Date(appt.start_datetime).toLocaleString(undefined, {
                        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
                      })}
                    </div>
                    <div className="unified-list-cell">{appt.vehicle}</div>
                    <div className="unified-list-cell">{appt.appointment_type_name}</div>
                    <div className="unified-list-cell">{appt.status}</div>
                  </div>
                ))
              ) : (
                <div className="unified-list-row">
                  <div className="unified-list-cell" style={{ gridColumn: '1 / -1', textAlign: 'center' }}>
                    No appointments found
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {showAppointmentModal && (
        <AppointmentModal
          appointment={selectedAppointment}
          prefilledStart={null}
          appointmentTypes={appointmentTypes}
          slotIntervalMinutes={schedulingSettings ? schedulingSettings.slot_interval_minutes : 30}
          onClose={handleAppointmentModalClose}
        />
      )}

      <NewWorkOrderModal
        showModal={showNewWorkOrderModal}
        toggleModal={toggleNewWorkOrderModal}
        onWorkOrderCreated={handleWorkOrderCreated}
        customerName={customer.name}
      />

      <NewJobModal
        showModal={showNewJobModal}
        toggleModal={toggleNewJobModal}
        onJobCreated={handleJobCreated}
        customerName={customer.name}
      />
      
      <WorkOrderModal 
        showModal={showModal} 
        toggleModal={() => setShowModal(false)} 
        workOrder={selectedWorkOrder}
        onWorkOrderUpdated={handleWorkOrderUpdate}
      />

      <JobModal 
        showModal={showJobModal} 
        toggleModal={() => setShowJobModal(false)} 
        job={selectedJob}
        onJobUpdated={handleJobUpdate}
      />

      <QuoteOrderModal 
        showModal={showQuoteModal} 
        toggleModal={() => setShowQuoteModal(false)} 
        quote={selectedQuote}
        onQuoteUpdated={handleQuoteUpdate}
        customerName={customer?.name}
      />
    </div>
  );
};

export default CustomerWorkOrderList;